// src/components/PayPeriodDetails.js

import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  IconButton,
  Box,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Container,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  ListItemText,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { 
  MdEdit, 
  MdDelete, 
  MdAdd, 
  MdSubject, 
  MdTaskAlt, 
  MdCalendarMonth, 
  MdOutlineToday,
  MdClear,
  MdCheckCircle,
  MdPerson
} from 'react-icons/md';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

const PayPeriodDetails = ({
  payPeriod,
  closeModal,
  createPayrollNote,
  updatePayrollNote,
  deletePayrollNote,
  createToDoItem,
  updateToDoItem,
  deleteToDoItem,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userRole } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0); // 0 for Notes, 1 for To-Dos
  const [expandedNoteForm, setExpandedNoteForm] = useState(false);
  const [expandedTodoForm, setExpandedTodoForm] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState(payPeriod?.notes || []);
  const [newNoteSubject, setNewNoteSubject] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  
  // To-Dos state
  const [todos, setTodos] = useState(payPeriod?.todos || []);
  const [newToDoDescription, setNewToDoDescription] = useState('');
  const [newToDoDueDate, setNewToDoDueDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState({
    note: false,
    todo: false
  });

  // Editing states
  const [editingNote, setEditingNote] = useState(null);
  const [editedNoteSubject, setEditedNoteSubject] = useState('');
  const [editedNoteContent, setEditedNoteContent] = useState('');
  const [editingToDo, setEditingToDo] = useState(null);
  const [editedToDoDescription, setEditedToDoDescription] = useState('');
  const [editedToDoDueDate, setEditedToDoDueDate] = useState('');

  // Acknowledge dialog state
  const [acknowledgeDialogOpen, setAcknowledgeDialogOpen] = useState(false);
  const [selectedNoteToAcknowledge, setSelectedNoteToAcknowledge] = useState(null);
  const [managerResponse, setManagerResponse] = useState('');

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  
  // Update local state when payPeriod changes
  useEffect(() => {
    if (payPeriod) {
      console.log("PayPeriodDetails updated with period:", {
        startDate: payPeriod.startDate,
        endDate: payPeriod.endDate,
        notesCount: payPeriod.notes?.length || 0,
        todosCount: payPeriod.todos?.length || 0
      });
      
      // Sort notes by creation date
      const sortedNotes = [...(payPeriod.notes || [])]
        .sort((a, b) => new Date(b.createdAt || b.updatedAt) - new Date(a.createdAt || a.updatedAt));
      setNotes(sortedNotes);
      
      // Normalize and sort todos
      const normalizedTodos = [...(payPeriod.todos || [])]
        .map(todo => ({
          ...todo,
          isCompleted: Boolean(todo.isCompleted),
          employeeId: todo.employeeId || payPeriod.employeeId
        }))
        .sort((a, b) => {
          // Sort by completion status first (incomplete first), then by due date
          if (a.isCompleted !== b.isCompleted) {
            return a.isCompleted ? 1 : -1;
          }
          if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate) - new Date(b.dueDate);
          }
          return 0;
        });
      
      setTodos(normalizedTodos);
    }
  }, [payPeriod]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Toggle form visibility
  const toggleNoteForm = () => {
    setExpandedNoteForm(!expandedNoteForm);
    if (!expandedNoteForm) {
      setExpandedTodoForm(false);
    }
  };
  
  const toggleTodoForm = () => {
    setExpandedTodoForm(!expandedTodoForm);
    if (!expandedTodoForm) {
      setExpandedNoteForm(false);
    }
  };
  
  // Handle snackbar
  const handleOpenSnackbar = (severity, message) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString;
    }
  };
  
  // Handle note actions
  const handleAddNote = async () => {
    if (!newNoteSubject.trim() && !newNoteContent.trim()) {
      handleOpenSnackbar('warning', 'Please enter a subject or content for the note');
      return;
    }

    if (!payPeriod.employeeId) {
      handleOpenSnackbar("error", "Employee ID is missing. Please log in again.");
      console.error("Missing employeeId:", payPeriod);
      return;
    }

    setSubmitting({ ...submitting, note: true });
    try {
      // Format the date to include the pay period's start date
      let noteDate;
      if (payPeriod.rawStartDate) {
        if (typeof payPeriod.rawStartDate === 'string') {
          noteDate = new Date(payPeriod.rawStartDate).toISOString().split('T')[0];
        } else if (payPeriod.rawStartDate instanceof Date) {
          noteDate = payPeriod.rawStartDate.toISOString().split('T')[0];
        } else {
          noteDate = new Date().toISOString().split('T')[0];
        }
      } else {
        noteDate = new Date().toISOString().split('T')[0];
      }
      
      const newNote = await createPayrollNote(
        newNoteSubject.trim(),
        newNoteContent.trim(),
        noteDate
      );

      setNotes(prevNotes => [newNote, ...prevNotes]);
      setNewNoteSubject('');
      setNewNoteContent('');
      setExpandedNoteForm(false);
      handleOpenSnackbar('success', 'Note added successfully');
    } catch (err) {
      console.error('Error adding note:', err);
      handleOpenSnackbar('error', err.message || 'Failed to add note. Please try again.');
    } finally {
      setSubmitting({ ...submitting, note: false });
    }
  };
  
  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditedNoteSubject(note.subject || '');
    setEditedNoteContent(note.content || '');
  };
  
  const handleUpdateNote = async () => {
    if (!editedNoteSubject.trim() || !editedNoteContent.trim()) {
      handleOpenSnackbar('warning', 'Subject and content are required.');
      return;
    }
    
    setLoading(true);
    try {
      await updatePayrollNote(
        editingNote.id, 
        editingNote.employeeId, 
        editedNoteSubject, 
        editedNoteContent, 
        editingNote.noteDate
      );
      
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.id === editingNote.id
            ? { ...note, subject: editedNoteSubject, content: editedNoteContent }
            : note
        )
      );
      
      setEditingNote(null);
      setEditedNoteSubject('');
      setEditedNoteContent('');
      handleOpenSnackbar('success', 'Note updated successfully!');
    } catch (err) {
      console.error('Update Note Error:', err);
      handleOpenSnackbar('error', err.response?.data?.error || 'Failed to update note.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      setLoading(true);
      try {
        await deletePayrollNote(noteId);
        setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
        handleOpenSnackbar('success', 'Note deleted successfully!');
      } catch (err) {
        console.error('Delete Note Error:', err);
        handleOpenSnackbar('error', err.response?.data?.error || 'Failed to delete note.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Handle todo actions
  const handleAddTodo = async () => {
    if (!newToDoDescription.trim() || !newToDoDueDate.trim()) {
      handleOpenSnackbar('warning', 'Please fill out all fields.');
      return;
    }

    if (!payPeriod.employeeId) {
      handleOpenSnackbar("error", "Employee ID is missing. Please log in again.");
      return;
    }

    setSubmitting({ ...submitting, todo: true });
    try {
      const result = await createToDoItem(
        newToDoDescription.trim(),
        newToDoDueDate
      );
      
      setTodos((prevTodos) => [result, ...prevTodos]);
      setNewToDoDescription('');
      setNewToDoDueDate(format(new Date(), 'yyyy-MM-dd'));
      setExpandedTodoForm(false);
      handleOpenSnackbar('success', 'To-Do item added successfully!');
    } catch (err) {
      console.error('Add To-Do Error:', err);
      handleOpenSnackbar('error', err.response?.data?.error || 'Failed to add to-do item.');
    } finally {
      setSubmitting({ ...submitting, todo: false });
    }
  };
  
  const handleEditTodo = (todo) => {
    setEditingToDo({
      ...todo,
      employeeId: todo.employeeId || payPeriod.employeeId
    });
    setEditedToDoDescription(todo.description || '');
    setEditedToDoDueDate(
      todo.dueDate ? format(new Date(todo.dueDate), 'yyyy-MM-dd') : ''
    );
  };
  
  const handleUpdateTodo = async () => {
    if (!editedToDoDescription.trim() || !editedToDoDueDate) {
      handleOpenSnackbar('warning', 'Description and due date are required.');
      return;
    }
    
    setLoading(true);
    try {
      await updateToDoItem(
        editingToDo.id,
        editingToDo.employeeId || payPeriod.employeeId,
        editedToDoDescription,
        editingToDo.isCompleted,
        editedToDoDueDate
      );
      
      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo.id === editingToDo.id
            ? { 
                ...todo, 
                description: editedToDoDescription, 
                dueDate: editedToDoDueDate 
              }
            : todo
        )
      );
      
      setEditingToDo(null);
      setEditedToDoDescription('');
      setEditedToDoDueDate('');
      handleOpenSnackbar('success', 'To-Do updated successfully!');
    } catch (err) {
      console.error('Update To-Do Error:', err);
      handleOpenSnackbar('error', err.response?.data?.error || 'Failed to update to-do item.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteTodo = async (todoId) => {
    if (window.confirm('Are you sure you want to delete this to-do item?')) {
      setLoading(true);
      try {
        await deleteToDoItem(todoId);
        setTodos((prevTodos) => prevTodos.filter((todo) => todo.id !== todoId));
        handleOpenSnackbar('success', 'To-Do deleted successfully!');
      } catch (err) {
        console.error('Delete To-Do Error:', err);
        handleOpenSnackbar('error', err.response?.data?.error || 'Failed to delete to-do item.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Toggle todo completion
  const handleToggleCompletion = async (todo) => {
    setLoading(true);
    try {
      // Optimistic update
      setTodos((prevTodos) =>
        prevTodos.map((t) =>
          t.id === todo.id ? { ...t, isCompleted: !todo.isCompleted } : t
        )
      );
      
      await updateToDoItem(
        todo.id,
        todo.employeeId || payPeriod.employeeId,
        todo.description,
        !todo.isCompleted,
        todo.dueDate
      );
      
      handleOpenSnackbar(
        'success', 
        `To-Do marked as ${!todo.isCompleted ? "complete" : "incomplete"}!`
      );
    } catch (err) {
      console.error('Toggle Completion Error:', err);
      
      // Revert optimistic update on failure
      setTodos((prevTodos) =>
        prevTodos.map((t) =>
          t.id === todo.id ? { ...t, isCompleted: todo.isCompleted } : t
        )
      );
      
      handleOpenSnackbar('error', err.response?.data?.error || 'Failed to update to-do status.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle note acknowledgment
  const handleAcknowledgeNote = (note) => {
    setSelectedNoteToAcknowledge(note);
    setManagerResponse('');
    setAcknowledgeDialogOpen(true);
  };
  
  const handleCloseAcknowledgeDialog = () => {
    setAcknowledgeDialogOpen(false);
    setSelectedNoteToAcknowledge(null);
    setManagerResponse('');
  };
  
  const handleSubmitAcknowledgment = async () => {
    if (!selectedNoteToAcknowledge) return;
    
    setLoading(true);
    try {
      const API_URL = process.env.NODE_ENV === 'development' 
        ? process.env.REACT_APP_API_URL_DEVELOPMENT 
        : process.env.REACT_APP_API_URL;
        
      const response = await fetch(`${API_URL}/payroll_notes/${selectedNoteToAcknowledge.id}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          managerResponse: managerResponse.trim() || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to acknowledge note');
      }
      
      const updatedNote = await response.json();
      
      // Update the note in the list
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === updatedNote.id ? updatedNote : note
        )
      );
      
      handleOpenSnackbar('success', 'Note acknowledged successfully!');
      handleCloseAcknowledgeDialog();
    } catch (err) {
      console.error('Error acknowledging note:', err);
      handleOpenSnackbar('error', err.message || 'Failed to acknowledge note');
    } finally {
      setLoading(false);
    }
  };

  // Use modern tabbed interface
  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      overflow: 'hidden'
    }}>
      {/* Header with Close Button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: 2, md: 3 },
        bgcolor: '#276a78',
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MdCalendarMonth size={24} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Pay Period Details
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {payPeriod.startDate} - {payPeriod.endDate}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={closeModal}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <MdClear size={24} />
        </IconButton>
      </Box>

      {/* Main Content Area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: { xs: 2, md: 4 }
      }}>
        {/* Simplified Tab Header */}
        <Box sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { 
              textTransform: 'none', 
              fontWeight: 500,
              fontSize: '1rem',
              py: 2,
              minHeight: 56,
              color: 'text.secondary',
              '&:hover': {
                color: '#276a78',
                bgcolor: 'rgba(39, 106, 120, 0.04)'
              }
            },
            '& .Mui-selected': {
              color: '#276a78',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#276a78',
              height: 3,
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MdSubject size={20} />
                <span>Notes</span>
                <Box component="span" sx={{ 
                  bgcolor: activeTab === 0 ? '#276a78' : 'divider',
                  color: activeTab === 0 ? 'white' : 'text.secondary',
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {notes.length}
                </Box>
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MdTaskAlt size={20} />
                <span>To-Dos</span>
                <Box component="span" sx={{ 
                  bgcolor: activeTab === 1 ? '#276a78' : 'divider',
                  color: activeTab === 1 ? 'white' : 'text.secondary',
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {todos.length}
                </Box>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content - Take remaining space */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        pb: 4
      }}>
        {/* Notes Tab Panel */}
        {activeTab === 0 && (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: { xs: '100%', md: 800 },
            mx: 'auto',
            width: '100%'
          }}>
            {/* Add Note Section */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3.5 },
                bgcolor: expandedNoteForm ? 'white' : 'rgba(39, 106, 120, 0.03)',
                borderRadius: 2,
                mb: 3,
                border: expandedNoteForm ? '2px solid rgba(39, 106, 120, 0.2)' : '1px solid rgba(39, 106, 120, 0.1)',
                transition: 'all 0.3s ease',
                boxShadow: expandedNoteForm ? '0 4px 12px rgba(39, 106, 120, 0.08)' : 'none'
              }}
            >
              {!expandedNoteForm ? (
                <Button
                  variant="outlined"
                  startIcon={<MdAdd size={22} />}
                  onClick={toggleNoteForm}
                  fullWidth
                  sx={{ 
                    justifyContent: 'center', 
                    py: 1.5,
                    px: 3,
                    color: '#276a78',
                    borderColor: 'rgba(39, 106, 120, 0.3)',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(39, 106, 120, 0.08)',
                      borderColor: '#276a78'
                    }
                  }}
                >
                  Add a new note
                </Button>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#276a78',
                        fontSize: '1.1rem'
                      }}
                    >
                      Add Note
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={toggleNoteForm}
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                      }}
                    >
                      <MdClear size={20} />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                      Subject
                    </Typography>
                    <TextField
                      placeholder="Enter note subject"
                      fullWidth
                      value={newNoteSubject}
                      onChange={(e) => setNewNoteSubject(e.target.value)}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#276a78',
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                      Content
                    </Typography>
                    <TextField
                      placeholder="Enter note content"
                      fullWidth
                      multiline
                      rows={4}
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#276a78',
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    pt: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.85rem',
                        display: { xs: 'none', sm: 'block' }
                      }}
                    >
                      Period: {payPeriod.startDate} - {payPeriod.endDate}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2,
                      justifyContent: { xs: 'stretch', sm: 'flex-end' },
                      '& > *': { xs: { flex: 1 }, sm: { flex: 'initial' } }
                    }}>
                      <Button
                        variant="outlined"
                        onClick={toggleNoteForm}
                        sx={{
                          px: 3,
                          py: 1.2,
                          borderColor: 'divider',
                          color: 'text.secondary',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            borderColor: 'text.secondary',
                            bgcolor: 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleAddNote}
                        disabled={submitting.note || !newNoteSubject.trim() || !newNoteContent.trim()}
                        startIcon={submitting.note ? <CircularProgress size={18} /> : <MdAdd size={20} />}
                        sx={{
                          bgcolor: '#276a78',
                          px: 3,
                          py: 1.2,
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#1d5761',
                            boxShadow: '0 2px 8px rgba(39, 106, 120, 0.3)'
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(39, 106, 120, 0.3)',
                            color: 'rgba(255, 255, 255, 0.7)'
                          }
                        }}
                      >
                        {submitting.note ? 'Adding...' : 'Add Note'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Notes List */}
            {notes.length > 0 ? (
              <List sx={{ p: 0 }}>
                {notes.map((note) => (
                  <Card 
                    key={note.id} 
                    sx={{ 
                      mb: 2, 
                      border: '1px solid rgba(39, 106, 120, 0.15)',
                      borderRadius: '12px',
                      '&:hover': { 
                        boxShadow: '0 4px 12px rgba(39, 106, 120, 0.1)',
                        borderColor: 'rgba(39, 106, 120, 0.3)'
                      }
                    }}
                  >
                    {editingNote && editingNote.id === note.id ? (
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h6" fontWeight="500">Edit Note</Typography>
                          <IconButton size="small" onClick={() => setEditingNote(null)}>
                            <MdClear />
                          </IconButton>
                        </Box>
                        
                        <TextField
                          label="Subject"
                          fullWidth
                          value={editedNoteSubject}
                          onChange={(e) => setEditedNoteSubject(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        
                        <TextField
                          label="Content"
                          fullWidth
                          multiline
                          rows={3}
                          value={editedNoteContent}
                          onChange={(e) => setEditedNoteContent(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setEditingNote(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpdateNote}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                          >
                            {loading ? 'Updating...' : 'Update Note'}
                          </Button>
                        </Box>
                      </CardContent>
                    ) : (
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" fontWeight="500" gutterBottom>
                              {note.subject || 'No subject'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {note.employeeName && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <MdPerson size={14} />
                                  From: {note.employeeName}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <MdOutlineToday size={14} />
                                {note.noteDate ? formatDate(note.noteDate) : 'No date'}
                              </Typography>
                              {note.isAcknowledged && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <MdCheckCircle size={14} />
                                  Acknowledged by {note.acknowledgedByName} on {formatDate(note.acknowledgedAt)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          
                          <Box>
                            {!note.isAcknowledged && (userRole === 'admin' || userRole === 'manager') && (
                              <Tooltip title="Acknowledge">
                                <IconButton 
                                  size="small" 
                                  color="success" 
                                  onClick={() => handleAcknowledgeNote(note)}
                                >
                                  <MdCheckCircle />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleEditNote(note)}
                              disabled={note.isAcknowledged}
                            >
                              <MdEdit />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={note.isAcknowledged}
                            >
                              <MdDelete />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </Typography>
                        
                        {note.isAcknowledged && note.managerResponse && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', p: 2, borderRadius: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                                Manager Response:
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {note.managerResponse}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <MdSubject size={40} color="#9e9e9e" />
                <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                  No notes found for this pay period
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={toggleNoteForm} 
                  startIcon={<MdAdd />}
                  sx={{ mt: 2 }}
                >
                  Add Your First Note
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* To-Dos Tab Panel */}
        {activeTab === 1 && (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: { xs: '100%', md: 800 },
            mx: 'auto',
            width: '100%'
          }}>
            {/* Add To-Do Section */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3.5 },
                bgcolor: expandedTodoForm ? 'white' : 'rgba(39, 106, 120, 0.03)',
                borderRadius: 2,
                mb: 3,
                border: expandedTodoForm ? '2px solid rgba(39, 106, 120, 0.2)' : '1px solid rgba(39, 106, 120, 0.1)',
                transition: 'all 0.3s ease',
                boxShadow: expandedTodoForm ? '0 4px 12px rgba(39, 106, 120, 0.08)' : 'none'
              }}
            >
              {!expandedTodoForm ? (
                <Button
                  variant="outlined"
                  startIcon={<MdAdd size={22} />}
                  onClick={toggleTodoForm}
                  fullWidth
                  sx={{ 
                    justifyContent: 'center', 
                    py: 1.5,
                    px: 3,
                    color: '#276a78',
                    borderColor: 'rgba(39, 106, 120, 0.3)',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(39, 106, 120, 0.08)',
                      borderColor: '#276a78'
                    }
                  }}
                >
                  Add a new to-do
                </Button>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#276a78',
                        fontSize: '1.1rem'
                      }}
                    >
                      Add To-Do
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={toggleTodoForm}
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                      }}
                    >
                      <MdClear size={20} />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                      Description
                    </Typography>
                    <TextField
                      placeholder="What needs to be done?"
                      fullWidth
                      value={newToDoDescription}
                      onChange={(e) => setNewToDoDescription(e.target.value)}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#276a78',
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                      Due Date
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      value={newToDoDueDate}
                      onChange={(e) => setNewToDoDueDate(e.target.value)}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#276a78',
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    pt: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.85rem',
                        display: { xs: 'none', sm: 'block' }
                      }}
                    >
                      Period: {payPeriod.startDate} - {payPeriod.endDate}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2,
                      justifyContent: { xs: 'stretch', sm: 'flex-end' },
                      '& > *': { xs: { flex: 1 }, sm: { flex: 'initial' } }
                    }}>
                      <Button
                        variant="outlined"
                        onClick={toggleTodoForm}
                        sx={{
                          px: 3,
                          py: 1.2,
                          borderColor: 'divider',
                          color: 'text.secondary',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            borderColor: 'text.secondary',
                            bgcolor: 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleAddTodo}
                        disabled={submitting.todo || !newToDoDescription.trim()}
                        startIcon={submitting.todo ? <CircularProgress size={18} /> : <MdAdd size={20} />}
                        sx={{
                          bgcolor: '#276a78',
                          px: 3,
                          py: 1.2,
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#1d5761',
                            boxShadow: '0 2px 8px rgba(39, 106, 120, 0.3)'
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(39, 106, 120, 0.3)',
                            color: 'rgba(255, 255, 255, 0.7)'
                          }
                        }}
                      >
                        {submitting.todo ? 'Adding...' : 'Add To-Do'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* To-Dos List */}
            {todos.length > 0 ? (
              <List sx={{ p: 0 }}>
                {todos.map((todo) => (
                  <Card 
                    key={todo.id} 
                    sx={{ 
                      mb: 2, 
                      border: '1px solid rgba(39, 106, 120, 0.15)',
                      borderRadius: '12px',
                      bgcolor: todo.isCompleted ? 'rgba(76, 175, 80, 0.04)' : 'white',
                      '&:hover': { 
                        boxShadow: '0 4px 12px rgba(39, 106, 120, 0.1)',
                        borderColor: 'rgba(39, 106, 120, 0.3)'
                      }
                    }}
                  >
                    {editingToDo && editingToDo.id === todo.id ? (
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h6" fontWeight="500">Edit To-Do</Typography>
                          <IconButton size="small" onClick={() => setEditingToDo(null)}>
                            <MdClear />
                          </IconButton>
                        </Box>
                        
                        <TextField
                          label="Description"
                          fullWidth
                          value={editedToDoDescription}
                          onChange={(e) => setEditedToDoDescription(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        
                        <TextField
                          label="Due Date"
                          type="date"
                          fullWidth
                          value={editedToDoDueDate}
                          onChange={(e) => setEditedToDoDueDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ mb: 2 }}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setEditingToDo(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpdateTodo}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                          >
                            {loading ? 'Updating...' : 'Update To-Do'}
                          </Button>
                        </Box>
                      </CardContent>
                    ) : (
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Checkbox
                            checked={!!todo.isCompleted}
                            onChange={() => handleToggleCompletion(todo)}
                            sx={{ 
                              mt: -1, 
                              color: todo.isCompleted ? 'success.main' : 'primary.main'
                            }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography 
                              variant="h6" 
                              fontWeight="500" 
                              sx={{
                                textDecoration: todo.isCompleted ? 'line-through' : 'none',
                                color: todo.isCompleted ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {todo.description}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <MdCalendarMonth size={14} />
                              Due: {todo.dueDate ? formatDate(todo.dueDate) : 'No due date'}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleEditTodo(todo)}
                              disabled={todo.isCompleted}
                            >
                              <MdEdit />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteTodo(todo.id)}
                            >
                              <MdDelete />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </List>
            ) : (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center', 
                bgcolor: 'rgba(39, 106, 120, 0.02)', 
                borderRadius: 2,
                border: '1px dashed rgba(39, 106, 120, 0.2)'
              }}>
                <MdTaskAlt size={48} style={{ color: '#9e9e9e' }} />
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'text.primary' }}>
                  No to-dos yet
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  Create your first to-do for this pay period
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={toggleTodoForm} 
                  startIcon={<MdAdd />}
                  sx={{ 
                    bgcolor: '#276a78',
                    '&:hover': { bgcolor: '#1d5761' }
                  }}
                >
                  Add Your First To-Do
                </Button>
              </Box>
            )}
          </Box>
        )}
        </Box>
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Acknowledge Dialog */}
      <Dialog 
        open={acknowledgeDialogOpen} 
        onClose={handleCloseAcknowledgeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#276a78', color: 'white' }}>
          Acknowledge Note
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedNoteToAcknowledge && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Subject:</strong> {selectedNoteToAcknowledge.subject}
              </Typography>
              
              {selectedNoteToAcknowledge.employeeName && (
                <Typography variant="body2" gutterBottom>
                  <strong>From:</strong> {selectedNoteToAcknowledge.employeeName}
                </Typography>
              )}
              
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(selectedNoteToAcknowledge.noteDate)}
              </Typography>
              
              <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedNoteToAcknowledge.content}
                </Typography>
              </Box>
              
              <TextField
                label="Response (optional)"
                fullWidth
                multiline
                rows={4}
                value={managerResponse}
                onChange={e => setManagerResponse(e.target.value)}
                placeholder="Add your response to this note..."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseAcknowledgeDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitAcknowledgment} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <MdCheckCircle />}
          >
            {loading ? 'Processing...' : 'Acknowledge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

PayPeriodDetails.propTypes = {
  payPeriod: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    employeeId: PropTypes.number.isRequired,
    rawStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    rawEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    notes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        employeeId: PropTypes.number.isRequired,
        employeeName: PropTypes.string,
        subject: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        noteDate: PropTypes.string.isRequired,
        createdAt: PropTypes.string,
        updatedAt: PropTypes.string,
      })
    ).isRequired,
    todos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
        dueDate: PropTypes.string,
        isCompleted: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      })
    ).isRequired,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  createPayrollNote: PropTypes.func.isRequired,
  updatePayrollNote: PropTypes.func.isRequired,
  deletePayrollNote: PropTypes.func.isRequired,
  createToDoItem: PropTypes.func.isRequired,
  updateToDoItem: PropTypes.func.isRequired,
  deleteToDoItem: PropTypes.func.isRequired,
};

export default PayPeriodDetails;